"use client";

import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { ProductCard } from "../shopCards/ProductCard";
import { Navigation, Pagination } from "swiper/modules";

import { getCategoryVariable, useT } from "@/context/I18nContext";
import { usePWA } from "@/utlis/usePWA";
import { useIsMobile } from "@/utlis/useIsMobile";
import { getCategoryBlurb } from "@/utlis/categoryBlurbs";

export default function CategoryProducts({ category }) {
	const isPWA = usePWA();
	const isMobile = useIsMobile();
	const { locale } = useT();

	const products = category?.products || [];
	const blurb = getCategoryBlurb(category?.slug, locale);

	if (!products.length) return null;

	if (isPWA || isMobile) {
		return (
			<>
				<style jsx>{`
					.category-mobile-section {
						width: 100%;
						overflow: hidden;
						padding-bottom: 20px !important;
					}

					.category-mobile-title-link {
						display: block;
						text-decoration: none;
						color: inherit;
					}

					.category-mobile-container {
						width: 100%;
					}

					.category-mobile-title {
						margin-top: 10px;
						margin-bottom: 14px;
						padding-left: 0;
						display: block;
					}

					.category-mobile-title .title {
						display: block;
						line-height: 1.2;
						margin: 0;
					}

					.category-blurb {
						font-size: 14px;
						color: #6b7280;
						margin: 4px 0 0;
						line-height: 1.2;
					}

					/* IMPORTANT:
					   Nu lăsăm grid-ul să întindă cardurile pe verticală. */
					.category-products-grid {
						width: 100%;
						display: grid;
						grid-template-columns: repeat(2, minmax(0, 1fr));
						gap: 12px;
						align-items: start;
						align-content: start;
					}

					/* Fiecare celulă rămâne strict la înălțimea conținutului */
					.category-product-grid-item {
						width: 100%;
						min-width: 0;
						height: fit-content;
						min-height: 0;
						align-self: start;
						display: block;
					}

					/*
					 * ProductCard poate avea CSS global cu height:100%,
					 * min-height sau flex-grow.
					 * Resetăm aici aceste comportamente doar în grid.
					 */
					.category-product-grid-item :global(.card-product) {
						height: auto !important;
						min-height: 0 !important;
						align-self: flex-start !important;
					}

					.category-product-grid-item :global(.card-product .card-product-wrapper) {
						height: auto;
						min-height: 0;
					}

					.category-product-grid-item :global(.card-product .card-product-info) {
						height: auto;
						min-height: 0;
					}

					/*
					 * Foarte important dacă ProductCard folosește flex
					 * și împinge prețul în partea de jos cu margin-top:auto.
					 */
					.category-product-grid-item :global(.card-product .price),
					.category-product-grid-item :global(.card-product .price-wrap) {
						margin-top: 0;
					}

					@media (max-width: 380px) {
						.category-products-grid {
							gap: 10px;
						}
					}
				`}</style>

				<section className='flat-spacing-4 pt_0 category-mobile-section'>
					<div className='container category-mobile-container'>
						<div className='flat-title category-mobile-title'>
							<Link href={`/${category.slug}`} className='category-mobile-title-link'>
								<h2 className='title'>
									{getCategoryVariable(category)}
								</h2>
							</Link>
							{blurb && <p className='category-blurb'>{blurb}</p>}
						</div>

						<div className='category-products-grid'>
							{products.map((product, i) => (
								<div
									key={product?.id || product?.slug || i}
									className='category-product-grid-item'>
									<ProductCard product={product} priority={i < 2} />
								</div>
							))}
						</div>
					</div>
				</section>
			</>
		);
	}

	return (
		<>
			<style jsx>{`
				.category-desktop-title {
					margin-top: 35px;
					margin-bottom: 35px;
					display: block;
					padding-left: 0;
				}

				.category-blurb {
					font-size: 14px;
					color: #6b7280;
					margin: 4px 0 0;
				}
			`}</style>

			<section className='flat-spacing-4 pt_0'>
				<div className='container'>
					<div className='flat-title category-desktop-title'>
						<Link href={`/${category.slug}`} className='category-mobile-title-link'>
							<h2 className='title'>
								{getCategoryVariable(category)}
							</h2>
						</Link>
						{blurb && <p className='category-blurb'>{blurb}</p>}
					</div>

					{products.length <= 4 ? (
						<div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
							{products.map((product, i) => (
								<div key={product?.id || product?.slug || i} style={{ width: "calc(25% - 22.5px)", minWidth: "220px" }}>
									<ProductCard product={product} priority={i < 2} />
								</div>
							))}
						</div>
					) : (
					<div className='hover-sw-nav hover-sw-2'>
						<Swiper
							dir='ltr'
							className='swiper tf-sw-product-sell wrap-sw-over'
							slidesPerView={4}
							spaceBetween={30}
							breakpoints={{
								1024: {
									slidesPerView: 4,
								},
								640: {
									slidesPerView: 3,
								},
								0: {
									slidesPerView: 2,
									spaceBetween: 15,
								},
							}}
							modules={[Navigation, Pagination]}
							navigation={{
								prevEl: ".snbp308",
								nextEl: ".snbn308",
							}}
							slidesPerGroup={1}
							speed={300}
							resistance={true}
							resistanceRatio={0.85}
							preventInteractionOnTransition={true}
							pagination={{
								clickable: true,
								el: ".spd308",
								type: "bullets",
							}}
							style={{
								paddingTop: "6px",
								paddingBottom: "16px",
							}}>
							{products.map((product, i) => (
								<SwiperSlide
									key={product?.id || product?.slug || i}
									className='swiper-slide'>
									<ProductCard product={product} />
								</SwiperSlide>
							))}
						</Swiper>

						<div className='nav-sw nav-next-slider nav-next-recent box-icon w_46 round snbp308'>
							<span className='icon icon-arrow-left' />
						</div>

						<div className='nav-sw nav-prev-slider nav-prev-recent box-icon w_46 round snbn308'>
							<span className='icon icon-arrow-right' />
						</div>

						<div className='sw-dots style-2 sw-pagination-recent justify-content-center spd308' />
					</div>
					)}
				</div>
			</section>
		</>
	);
}